import { mobileClient } from '@/mobile';
import { useUserStore } from '@/store';
import { PermissionPageKey } from '@/types/permission';
import { message } from 'ant-design-vue';
import type { Router, RouteRecordNormalized, RouteRecordRaw } from 'vue-router';

const WHITE_LIST = ['/404', '/403', '/feishu-redirect'];

export function setupPermissionGuard(router: Router) {
  router.beforeEach(async (to) => {
    if (WHITE_LIST.includes(to.path)) {
      return true;
    }
    const userStore = useUserStore();
    const user = await userStore.getCurrent();
    // 匹配移动端下的路径，跳过权限验证，又 mobileGuard 负责
    const mobileRes = mobileClient.permissionGuard(to, { user });
    if (mobileRes) {
      return mobileRes;
    }

    if (user) {
      if (to.path === '/login') {
        return { path: '/' };
      }

      if (!userStore.hasRLNPermission(to)) {
        // 智能重定向覆盖：组织用户 + OD 外包用户（test-od / dev-od）
        // OD 外包无 organizationIds，但工作台等入口硬编码跳 ProjectDashboard（OD 白名单已移除），
        // 不放宽会直接落 403；纳入后会重定向到 findAccessibleChildRoute 返回的首个可访问子页。
        // 非 OD 非组织用户仍保留 403 行为（沿用 f01efd87 保守设计，动机未考古清楚，暂不动）。
        if (userStore.isOrganizationUser() || userStore.isOdUser()) {
          // 检查是否是访问父级路由（可能是重定向导致的）
          const isFromRedirect = to.query._redirect === 'true';
          // 检查顶层路由（site级别）是否有子路由，而不是最后一个匹配的路由
          const topRoute = to.matched[0];
          const hasChildren = topRoute?.children && topRoute.children.length > 0;

          if (!isFromRedirect && hasChildren) {
            // OD 外包固定落点（dev-od / test-od）：项目空间内默认跳 issue/list，不走通用算法。
            // 动机：OD 核心工作流是缺陷处理；研发子 tab（test-programme / test-plan 等）
            // 新项目下后端返回空列表，findAccessibleChildRoute 按 route 顺序会命中
            // "空表页面"误导用户以为页面坏了（2026-04-22 dev-od E2E 反馈）。
            // 仅在项目空间（params.pkey 存在）生效，非项目路由走原通用逻辑。
            const pkey = to.params?.pkey;
            if (userStore.isOdUser() && pkey) {
              return {
                name: PermissionPageKey.ProjectIssueList,
                params: to.params,
                query: { ...to.query, _redirect: 'true' },
              };
            }

            // 尝试找到用户有权限的第一个子路由
            const accessibleChild = findAccessibleChildRoute(to.matched, userStore);
            if (accessibleChild && accessibleChild !== to.name) {
              // 添加标记，避免无限重定向
              return {
                name: accessibleChild,
                params: to.params,
                query: { ...to.query, _redirect: 'true' },
              };
            }
          }
        }

        message.error('您没有权限访问此页面');
        return { path: '/403' };
      }
      return true;
    }
    if (to.path !== '/login') {
      message.error('请先登录');
      return { path: 'login', query: { ...to.query, redirect: to.path } };
    }
    return true;
  });
}

/**
 * @description 查找用户有权限访问的第一个子路由
 * 当用户访问父级路由被重定向到没有权限的子路由时，尝试找到用户有权限的第一个子路由
 */
function findAccessibleChildRoute(
  matched: RouteRecordNormalized[],
  userStore: ReturnType<typeof useUserStore>,
): PermissionPageKey | null {
  // 从最顶层的路由开始查找（通常是 site 级别的路由）
  const topRoute = matched[0];
  if (!topRoute || !topRoute.children || topRoute.children.length === 0) {
    return null;
  }

  const pagePermissions = userStore.pagePermissions;
  const currentUser = userStore.current;
  if (!currentUser) {
    return null;
  }

  // 并集：重定向找"任一角色可进"的子路由（可达性按并集，与菜单一致）
  const roleIds = currentUser.roles.map((r) => r.id);

  // 递归查找所有子路由，找到第一个用户有权限访问的页面
  const findFirstAccessible = (routes: (RouteRecordNormalized | RouteRecordRaw)[]): PermissionPageKey | null => {
    for (const route of routes) {
      const routeName = route.name as PermissionPageKey;
      if (routeName) {
        // 检查角色权限
        const hasRolePermission = !route.meta?.roles || (route.meta.roles as string[]).some((r) => roleIds.includes(r));
        // 检查页面权限
        const hasPagePermission = pagePermissions.includes(routeName);

        if (hasRolePermission && hasPagePermission) {
          // 如果有子路由，继续深入查找第一个叶子节点
          if (route.children && route.children.length > 0) {
            const child = findFirstAccessible(route.children);
            if (child) {
              return child;
            }
          }
          // 如果没有子路由或子路由都没权限，返回当前路由
          // 只返回有实际组件的路由（不是纯粹的父级路由）
          const components = 'components' in route ? route.components : route.component;
          if (components) {
            // 检查路由路径是否包含额外的动态参数（除了已有的参数）
            // 跳过需要额外参数的路由（如详情页 :id, :type 等）
            const routePath = route.path || '';
            const dynamicParams = routePath.match(/:\w+/g) || [];

            // 过滤掉 pkey 参数（项目空间的基础参数）
            const extraParams = dynamicParams.filter((param) => param !== ':pkey');

            // 只返回不需要额外参数的路由
            if (extraParams.length === 0) {
              return routeName;
            }
            // 有额外参数（如 :id, :type），跳过这个路由，继续查找
          }
        }
      }
      // 如果当前路由没权限，但有子路由，继续查找子路由
      if (route.children && route.children.length > 0) {
        const child = findFirstAccessible(route.children);
        if (child) {
          return child;
        }
      }
    }
    return null;
  };

  return findFirstAccessible(topRoute.children);
}
